import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { payrollAPI, employeeAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatMinutesToHours } from '../../lib/utils';
import { DollarSign, Calculator, FileText, Loader2 } from 'lucide-react';

const PayrollManagement = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const fetchData = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const [configRes, recordsRes, empRes] = await Promise.all([
        payrollAPI.getConfig(user.company_id).catch(() => ({ data: null })),
        payrollAPI.getRecords(user.company_id).catch(() => ({ data: [] })),
        employeeAPI.getAll(user.company_id)
      ]);
      setConfig(configRes.data);
      setRecords(recordsRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const response = await payrollAPI.generate(user.company_id, periodStart, periodEnd);
      toast.success(response.data.message);
      setIsGenerateOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payroll');
    } finally {
      setGenerating(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };

  if (loading) {
    return (
      <DashboardLayout title="Payroll Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Payroll Management" 
      subtitle="Generate and manage employee payroll"
    >
      {/* Config Card */}
      {config && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              PH Compliance Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-secondary rounded-lg">
                <p className="text-xs text-muted-foreground">SSS (Employee)</p>
                <p className="font-semibold">{(config.sss_employee_rate * 100).toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-secondary rounded-lg">
                <p className="text-xs text-muted-foreground">PhilHealth</p>
                <p className="font-semibold">{(config.philhealth_rate * 100).toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-secondary rounded-lg">
                <p className="text-xs text-muted-foreground">Pag-IBIG (Employee)</p>
                <p className="font-semibold">{(config.pagibig_employee_rate * 100).toFixed(1)}%</p>
              </div>
              <div className="p-3 bg-secondary rounded-lg">
                <p className="text-xs text-muted-foreground">Cutoff Day</p>
                <p className="font-semibold">Day {config.cutoff_day}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end mb-6">
        <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="generate-payroll-btn">
              <DollarSign className="h-4 w-4" />
              Generate Payroll
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Generate Payroll</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGeneratePayroll} className="space-y-4">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  required
                />
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                This will calculate payroll for all employees based on their attendance records during this period.
              </div>
              <Button type="submit" className="w-full" disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Payroll'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Payroll Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Payroll Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Days Present</TableHead>
                <TableHead>Worked Hours</TableHead>
                <TableHead>OT Hours</TableHead>
                <TableHead>Gross Pay</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Net Pay</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id} data-testid={`payroll-row-${record.id}`}>
                  <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>
                  <TableCell className="text-sm">
                    {record.period_start} - {record.period_end}
                  </TableCell>
                  <TableCell>{record.days_present}</TableCell>
                  <TableCell>{record.total_worked_hours}h</TableCell>
                  <TableCell>{record.total_overtime_hours}h</TableCell>
                  <TableCell>{formatCurrency(record.gross_pay)}</TableCell>
                  <TableCell className="text-red-600">{formatCurrency(record.total_deductions)}</TableCell>
                  <TableCell className="font-semibold text-emerald-600">
                    {formatCurrency(record.net_pay)}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      record.status === 'paid' ? 'status-approved' :
                      record.status === 'completed' ? 'status-present' :
                      'status-pending'
                    }>
                      {record.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {records.length === 0 && (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No payroll records yet</p>
              <p className="text-sm text-muted-foreground">Generate your first payroll to see records here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default PayrollManagement;
